import { Modal as AntModal, type ModalProps as AntModalProps } from 'antd';
import classNames from 'classnames';
import React, { memo } from 'react';

import Button, { ButtonProps } from '@fmbt/ui/Button';
import { FiExternalLink, WarningIcon } from '@fmbt/ui/icons';
import { HStack, VStack } from '@fmbt/ui/layout';
import Text from '@fmbt/ui/Text';
import { red } from 'style/colour-palette';

import './Modal.less';

// ==============================
// TYPES
// ==============================
type ModalDismissButtonVisibility = boolean | 'topRightClose';

type ModalProps = {
  /** Empty means center. */
  align?: 'start';
  children?: JSX.Element;
  /** Opens in a new tab with external-link icon; navigation via `href`, not `onConfirm`. */
  confirmHref?: string;
  confirmText: string;
  dismissText?: string;
  hasStackedActions?: boolean;
  /**
   * Footer dismiss CTA visibility. `true` hides Cancel with no other dismiss affordance.
   * `'topRightClose'` hides Cancel and shows the top-right close control (same as mask dismiss).
   */
  hideDismissButton?: ModalDismissButtonVisibility;
  isCancelButtonDisabled?: boolean;
  isConfirmButtonDangerous?: boolean;
  isConfirmButtonDisabled?: boolean;
  isConfirmButtonLoading?: boolean;
  isOpen?: AntModalProps['open'];
  /**
   * When false, the dialog cannot be dismissed via mask click or Escape.
   * Pair with `hideDismissButton` for blocking flows.
   */
  maskClosable?: AntModalProps['maskClosable'];
  onConfirm?: AntModalProps['onOk'];
  onDismiss?: AntModalProps['onCancel'];
  showWarningIcon?: boolean;
  size?: 480 | 360 | 320;
  title: string;
};

// ==============================
// CONSTANTS
// ==============================
/** Matches the implementation of NewTabLink.tsx */
const EXTERNAL_LINK_ICON_STYLE: React.CSSProperties = {
  marginLeft: 4,
  verticalAlign: 'text-bottom'
};

/** Matches @modal-footer-button-vertical-gap in Modal.less */
const FOOTER_BUTTONS_VSTACK_GAP = 12;

/** Matches @modal-footer-button-horizontal-gap in Modal.less */
const FOOTER_BUTTONS_HSTACK_GAP = 16;

// ==============================
// COMPONENTS
// ==============================
function Modal({
  align,
  children,
  confirmHref,
  confirmText,
  dismissText,
  hasStackedActions = false,
  hideDismissButton = false,
  isCancelButtonDisabled = false,
  isConfirmButtonDangerous = false,
  isConfirmButtonDisabled = false,
  isConfirmButtonLoading = false,
  isOpen,
  maskClosable = true,
  onConfirm,
  onDismiss,
  showWarningIcon,
  size = 480,
  title: titleText
}: ModalProps): JSX.Element {
  const showTopRightClose = hideDismissButton === 'topRightClose';
  const hideFooterDismiss = hideDismissButton === true || showTopRightClose;

  const dismissButton = hideFooterDismiss ? null : (
    <Button
      disabled={isCancelButtonDisabled}
      onClick={onDismiss}
      size="medium"
      type="tertiary"
      width="block"
    >
      {dismissText || 'Cancel'}
    </Button>
  );

  const confirmButtonProps: ButtonProps = {
    children: confirmText,
    disabled: isConfirmButtonDisabled,
    loading: isConfirmButtonLoading,
    onClick: onConfirm,
    size: 'medium',
    type: isConfirmButtonDangerous ? 'danger-filled' : 'primary',
    width: 'block'
  };
  if (confirmHref) {
    confirmButtonProps.children = (
      <>
        {confirmText}
        {/* Matches the implementation of NewTabLink.tsx */}
        <FiExternalLink size={18} style={EXTERNAL_LINK_ICON_STYLE} />
      </>
    );
    confirmButtonProps.href = confirmHref;
    confirmButtonProps.rel = 'noopener noreferrer';
    confirmButtonProps.target = '_blank';
  }
  const confirmButton = <Button {...confirmButtonProps} />;

  const title = showWarningIcon ? (
    <HStack gap={8} align="end">
      <div className="Modal-do-not-shrink-icon"><WarningIcon color={red} size={24} /></div>
      <Text v="modal-title">{titleText}</Text>
    </HStack>
  ) : (
    <Text v="modal-title">{titleText}</Text>
  );

  return (
    <AntModal
      centered
      className={classNames(
        'Modal',
        `Modal--size-${size}`
      )}
      closable={showTopRightClose}
      footer={null} // We roll our own footer below.
      keyboard={maskClosable}
      maskClosable={maskClosable}
      onCancel={onDismiss}
      open={isOpen}
      width={size}
    >
      {showTopRightClose && <div aria-hidden className="Modal-close-spacer" />}
      <div className={align === 'start' ? 'Modal-body--start' : 'Modal-body--center'}>
        {title}
        {children}
      </div>
      <div className="Modal-footer">
        {hasStackedActions ? (
          <VStack gap={FOOTER_BUTTONS_VSTACK_GAP}>
            {/* Top catches attention first */}
            {confirmButton}
            {dismissButton}
          </VStack>
        ) : (
          <HStack block gap={FOOTER_BUTTONS_HSTACK_GAP}>
            {dismissButton}
            {/* Right is easier to reach */}
            {confirmButton}
          </HStack>
        )}
      </div>
    </AntModal>
  );
}

export default memo(Modal);
export type { ModalDismissButtonVisibility, ModalProps };
